"use client";

import { c as _c } from "react/compiler-runtime";
import { ChevronUp, CreditCard, Edit2, ExternalLink, ImageIcon, LogOut, MoreVerticalIcon, Trash2 } from "lucide-react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { List, type RowComponentProps } from "react-window";
import { CreditsDisplay } from "@/components/credits/credits-display";
import { ThemeToggle } from "@/components/landing/theme-toggle";
import { SidebarLoungePoints } from "@/components/lounge/sidebar-points";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader, SidebarMenu, SidebarMenuAction, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@/components/ui/sidebar";
import { Wordmark } from "@/components/ui/wordmark";
import { useOrganization } from "@/hooks/useOrganization";
import { useDeleteImageHistory, useRenameImageHistory } from "@/hooks/usePlaygroundHistory";
import { useUser } from "@/hooks/useUser";
import { clearLastUsedProjectCookiesAction } from "@/lib/actions/project";
import { useAuth } from "@/lib/auth-client";
import { withOrgParam } from "@/lib/utils";
import { HistorySkeleton } from "./history-skeleton";
import { OrganizationSwitcher } from "./organization-switcher";
import { SidebarChatSearch, SidebarNewAction } from "./sidebar-actions";
import { StudioNav } from "./studio-nav";
import type { GalleryItem } from "@/lib/image-gen";
import type { Organization } from "@/lib/types";
interface ImageSidebarProps {
  galleryItems: GalleryItem[];
  isHistoryLoading?: boolean;
  onNewChat: () => void;
  onItemClick: (itemId: string) => void;
  organizations: Organization[];
  selectedOrganization: Organization | null;
  onSelectOrganization: (organization: Organization | null) => void;
  currentItemId?: string | null;
  className?: string;
}
type ImageHistoryRow = {
  type: "header";
  key: string;
  title: string;
} | {
  type: "item";
  key: string;
  item: GalleryItem;
} | {
  type: "spacer";
  key: string;
};
const ROW_HEIGHT_HEADER = 28;
const ROW_HEIGHT_SPACER = 6;
const ROW_HEIGHT_ITEM = 60;
interface ImageHistoryRowProps {
  rows: ImageHistoryRow[];
  currentItemId?: string | null;
  editingId: string | null;
  editPrompt: string;
  pendingFocusId: string | null;
  onItemClick: (itemId: string) => void;
  onEditPromptChange: (value: string) => void;
  onSaveEdit: (id: string, original: string) => void;
  onCancelEdit: () => void;
  onDeleteItem: (id: string) => void;
  onStartEdit: (id: string, prompt: string) => void;
  onEditFocused: () => void;
}
function getImageHistoryRowHeight(index: number, {
  rows
}: ImageHistoryRowProps): number {
  const row = rows[index];
  if (row?.type === "header") {
    return ROW_HEIGHT_HEADER;
  }
  if (row?.type === "spacer") {
    return ROW_HEIGHT_SPACER;
  }
  return ROW_HEIGHT_ITEM;
}
function EditImagePromptInput(t0) {
  const $ = _c(17);
  const {
    itemId,
    value,
    original,
    shouldFocus,
    onChange,
    onSave,
    onCancel,
    onFocused
  } = t0;
  const inputRef = useRef(null);
  let t1;
  let t2;
  if ($[0] !== onFocused || $[1] !== shouldFocus) {
    t1 = () => {
      if (!shouldFocus || !inputRef.current || document.activeElement === inputRef.current) {
        return;
      }
      const el = inputRef.current;
      el.focus();
      const len = el.value.length;
      el.setSelectionRange(len, len);
      onFocused();
    };
    t2 = [shouldFocus, onFocused];
    $[0] = onFocused;
    $[1] = shouldFocus;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] !== onChange) {
    t3 = e => onChange(e.target.value);
    $[4] = onChange;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== itemId || $[7] !== onSave || $[8] !== original) {
    t4 = () => onSave(itemId, original);
    $[6] = itemId;
    $[7] = onSave;
    $[8] = original;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== onCancel) {
    t5 = e_0 => {
      if (e_0.key === "Enter") {
        e_0.preventDefault();
        e_0.currentTarget.blur();
      }
      if (e_0.key === "Escape") {
        e_0.preventDefault();
        onCancel();
      }
    };
    $[10] = onCancel;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t3 || $[13] !== t4 || $[14] !== t5 || $[15] !== value) {
    t6 = <Input ref={inputRef} value={value} onChange={t3} onBlur={t4} onKeyDown={t5} className="h-7 text-sm border-none px-1 focus-visible:ring-0 bg-transparent" />;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
    $[15] = value;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
}
function ImageHistoryRowComponent(t0) {
  const $ = _c(38);
  const {
    ariaAttributes,
    index,
    style,
    rows,
    currentItemId,
    editingId,
    editPrompt,
    pendingFocusId,
    onItemClick,
    onEditPromptChange,
    onSaveEdit,
    onCancelEdit,
    onDeleteItem,
    onStartEdit,
    onEditFocused
  } = t0;
  const row = rows[index];
  if (!row) {
    return null;
  }
  if (row.type === "spacer") {
    let t1;
    if ($[0] !== style) {
      t1 = <div style={style} aria-hidden="true" />;
      $[0] = style;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  if (row.type === "header") {
    let t1;
    if ($[2] !== row.title) {
      t1 = <div className="px-5 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider group-data-[collapsible=icon]:hidden">{row.title}</div>;
      $[2] = row.title;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    let t2;
    if ($[4] !== ariaAttributes || $[5] !== style || $[6] !== t1) {
      t2 = <div {...ariaAttributes} style={style}>{t1}</div>;
      $[4] = ariaAttributes;
      $[5] = style;
      $[6] = t1;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    return t2;
  }
  const {
    item
  } = row;
  const isEditing = editingId === item.id;
  const isActive = currentItemId === item.id;
  let t1;
  if ($[8] !== item.models) {
    t1 = item.models.every(_temp);
    $[8] = item.models;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  const isSaved = t1;
  const firstImage = item.models[0]?.images[0];
  const thumbnailSrc = item.thumbnailUrl ?? (firstImage ? `data:${firstImage.mediaType};base64,${firstImage.base64}` : null);
  let t2;
  if ($[10] !== editPrompt || $[11] !== isActive || $[12] !== isEditing || $[13] !== item.id || $[14] !== item.prompt || $[15] !== item.timestamp || $[16] !== onCancelEdit || $[17] !== onEditFocused || $[18] !== onEditPromptChange || $[19] !== onItemClick || $[20] !== onSaveEdit || $[21] !== pendingFocusId || $[22] !== thumbnailSrc) {
    t2 = isEditing ? <div className="flex h-full w-full items-center rounded-md px-2 pr-8 text-left text-sm ring-sidebar-ring bg-sidebar-accent text-sidebar-accent-foreground"><EditImagePromptInput itemId={item.id} value={editPrompt} original={item.prompt} shouldFocus={pendingFocusId === item.id} onChange={onEditPromptChange} onSave={onSaveEdit} onCancel={onCancelEdit} onFocused={onEditFocused} /></div> : <SidebarMenuButton onClick={() => onItemClick(item.id)} isActive={isActive} className="h-full! w-full justify-start group relative pr-2 !transition-none group-hover/image-row:pr-9" type="button"><div className="flex items-start gap-2 min-w-0 w-full">{thumbnailSrc && <img src={thumbnailSrc} alt="Generated image thumbnail" loading="lazy" className="h-8 w-8 shrink-0 rounded border object-cover mt-0.5" />}<div className="flex-1 min-w-0"><div className="truncate text-sm font-medium mb-0.5">{item.prompt}</div><div className="text-xs text-muted-foreground">{new Date(item.timestamp).toLocaleTimeString([], {
              hour: "numeric",
              minute: "2-digit"
            })}</div></div></div></SidebarMenuButton>;
    $[10] = editPrompt;
    $[11] = isActive;
    $[12] = isEditing;
    $[13] = item.id;
    $[14] = item.prompt;
    $[15] = item.timestamp;
    $[16] = onCancelEdit;
    $[17] = onEditFocused;
    $[18] = onEditPromptChange;
    $[19] = onItemClick;
    $[20] = onSaveEdit;
    $[21] = pendingFocusId;
    $[22] = thumbnailSrc;
    $[23] = t2;
  } else {
    t2 = $[23];
  }
  let t3;
  if ($[24] !== isEditing || $[25] !== isSaved || $[26] !== item.id || $[27] !== item.prompt || $[28] !== onDeleteItem || $[29] !== onStartEdit) {
    t3 = !isEditing && isSaved && <div className="absolute right-1 top-1/2 flex -translate-y-1/2 items-center gap-0.5"><DropdownMenu><DropdownMenuTrigger asChild={true}><SidebarMenuAction type="button" onClick={_temp2} className="pointer-events-none static hidden h-7 w-7 cursor-pointer opacity-0 group-hover/image-row:flex group-hover/image-row:pointer-events-auto group-hover/image-row:opacity-100 data-[state=open]:flex data-[state=open]:pointer-events-auto data-[state=open]:opacity-100"><MoreVerticalIcon className="h-3.5 w-3.5" /></SidebarMenuAction></DropdownMenuTrigger><DropdownMenuContent align="end" className="w-40"><DropdownMenuItem onClick={e_0 => {
            e_0.stopPropagation();
            onStartEdit(item.id, item.prompt);
          }} className="flex items-center gap-2"><Edit2 className="h-4 w-4" />Rename</DropdownMenuItem><DropdownMenuItem onClick={e_1 => {
            e_1.stopPropagation();
            onDeleteItem(item.id);
          }} className="flex items-center gap-2 text-destructive focus:text-destructive"><Trash2 className="h-4 w-4" />Delete</DropdownMenuItem></DropdownMenuContent></DropdownMenu></div>;
    $[24] = isEditing;
    $[25] = isSaved;
    $[26] = item.id;
    $[27] = item.prompt;
    $[28] = onDeleteItem;
    $[29] = onStartEdit;
    $[30] = t3;
  } else {
    t3 = $[30];
  }
  let t4;
  if ($[31] !== t2 || $[32] !== t3) {
    t4 = <div className="relative h-full px-2 pb-1"><div className="group/image-row relative h-full">{t2}{t3}</div></div>;
    $[31] = t2;
    $[32] = t3;
    $[33] = t4;
  } else {
    t4 = $[33];
  }
  let t5;
  if ($[34] !== ariaAttributes || $[35] !== style || $[36] !== t4) {
    t5 = <div {...ariaAttributes} style={style}>{t4}</div>;
    $[34] = ariaAttributes;
    $[35] = style;
    $[36] = t4;
    $[37] = t5;
  } else {
    t5 = $[37];
  }
  return t5;
}
function _temp2(e) {
  e.stopPropagation();
}
function _temp(m) {
  return !m.isLoading;
}
function groupItemsByDate(items: GalleryItem[]) {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const lastWeek = new Date(today);
  lastWeek.setDate(lastWeek.getDate() - 7);
  const groups = {
    today: [] as GalleryItem[],
    yesterday: [] as GalleryItem[],
    lastWeek: [] as GalleryItem[],
    older: [] as GalleryItem[]
  };
  items.forEach(item => {
    const itemDate = new Date(item.timestamp);
    if (itemDate.toDateString() === today.toDateString()) {
      groups.today.push(item);
    } else if (itemDate.toDateString() === yesterday.toDateString()) {
      groups.yesterday.push(item);
    } else if (itemDate >= lastWeek) {
      groups.lastWeek.push(item);
    } else {
      groups.older.push(item);
    }
  });
  return groups;
}
export function ImageSidebar(t0) {
  const $ = _c(126);
  const {
    galleryItems,
    isHistoryLoading: t1,
    onNewChat,
    onItemClick,
    organizations,
    selectedOrganization,
    onSelectOrganization,
    currentItemId,
    className
  } = t0;
  const isHistoryLoading = t1 === undefined ? false : t1;
  let switcherOrganizations;
  let t2;
  if ($[0] !== organizations || $[1] !== selectedOrganization?.id) {
    switcherOrganizations = organizations.filter(_temp3);
    t2 = switcherOrganizations.find(org_0 => org_0.id === selectedOrganization?.id) ?? null;
    $[0] = organizations;
    $[1] = selectedOrganization?.id;
    $[2] = switcherOrganizations;
    $[3] = t2;
  } else {
    switcherOrganizations = $[2];
    t2 = $[3];
  }
  const switcherSelectedOrganization = t2;
  const listContainerRef = useRef(null);
  const router = useRouter();
  const searchParams = useSearchParams();
  const orgIdParam = searchParams.get("orgId");
  const withOrg = path => withOrgParam(path, orgIdParam);
  const posthog = usePostHog();
  const {
    state: sidebarState,
    isMobile
  } = useSidebar();
  const {
    user,
    isLoading: isUserLoading
  } = useUser();
  const {
    signOut
  } = useAuth();
  const {
    organization,
    isLoading: isOrgLoading
  } = useOrganization();
  let t3;
  if ($[4] !== posthog || $[5] !== router || $[6] !== signOut) {
    t3 = async () => {
      posthog.reset();
      try {
        await clearLastUsedProjectCookiesAction();
      } catch {}
      await signOut({
        fetchOptions: {
          onSuccess: () => {
            router.push(process.env.NODE_ENV === "development" ? "http://localhost:3003/login" : "https://chat.llmgateway.io/login");
          }
        }
      });
    };
    $[4] = posthog;
    $[5] = router;
    $[6] = signOut;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const logout = t3;
  const renameItem = useRenameImageHistory();
  const deleteItem = useDeleteImageHistory();
  const [editingId, setEditingId] = useState(null);
  const [editPrompt, setEditPrompt] = useState("");
  const [pendingFocusId, setPendingFocusId] = useState(null);
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = (id, prompt) => {
      setEditingId(id);
      setEditPrompt(prompt);
      setPendingFocusId(id);
    };
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const startEdit = t4;
  let t5;
  if ($[9] !== editPrompt || $[10] !== renameItem) {
    t5 = (id_0, original) => {
      const next = editPrompt.trim();
      if (next && next !== original) {
        renameItem.mutate({
          params: {
            path: {
              id: id_0
            }
          },
          body: {
            prompt: next
          }
        });
      }
      setEditingId(null);
      setEditPrompt("");
      setPendingFocusId(null);
    };
    $[9] = editPrompt;
    $[10] = renameItem;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const saveEdit = t5;
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setEditingId(null);
      setEditPrompt("");
      setPendingFocusId(null);
    };
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const cancelEdit = t6;
  let t7;
  if ($[13] !== deleteItem) {
    t7 = id_1 => {
      deleteItem.mutate({
        params: {
          path: {
            id: id_1
          }
        }
      });
    };
    $[13] = deleteItem;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const handleDeleteItem = t7;
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      setPendingFocusId(null);
    };
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const onEditFocused = t8;
  let t9;
  if ($[16] !== galleryItems) {
    t9 = [...galleryItems].sort(_temp4);
    $[16] = galleryItems;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const sortedItems = t9;
  let t10;
  if ($[18] !== sortedItems) {
    t10 = groupItemsByDate(sortedItems);
    $[18] = sortedItems;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const itemGroups = t10;
  let rows;
  if ($[20] !== itemGroups.lastWeek || $[21] !== itemGroups.older || $[22] !== itemGroups.today || $[23] !== itemGroups.yesterday) {
    const groups = [{
      title: "Today",
      items: itemGroups.today
    }, {
      title: "Yesterday",
      items: itemGroups.yesterday
    }, {
      title: "Last 7 days",
      items: itemGroups.lastWeek
    }, {
      title: "Older",
      items: itemGroups.older
    }];
    rows = [];
    groups.forEach((t11, groupIndex) => {
      const {
        title,
        items: groupedItems
      } = t11;
      if (groupedItems.length === 0) {
        return;
      }
      rows.push({
        type: "header",
        key: `header-${title}`,
        title
      });
      groupedItems.forEach(item => {
        rows.push({
          type: "item",
          key: `item-${item.id}`,
          item
        });
      });
      const hasNextGroupWithItems = groups.slice(groupIndex + 1).some(_temp5);
      if (hasNextGroupWithItems) {
        rows.push({
          type: "spacer",
          key: `spacer-${title}`
        });
      }
    });
    $[20] = itemGroups.lastWeek;
    $[21] = itemGroups.older;
    $[22] = itemGroups.today;
    $[23] = itemGroups.yesterday;
    $[24] = rows;
  } else {
    rows = $[24];
  }
  const historyRows = rows;
  let t11;
  if ($[25] !== currentItemId || $[26] !== editPrompt || $[27] !== editingId || $[28] !== handleDeleteItem || $[29] !== historyRows || $[30] !== onItemClick || $[31] !== pendingFocusId || $[32] !== saveEdit) {
    t11 = {
      rows: historyRows,
      currentItemId,
      editingId,
      editPrompt,
      pendingFocusId,
      onItemClick,
      onEditPromptChange: setEditPrompt,
      onSaveEdit: saveEdit,
      onCancelEdit: cancelEdit,
      onDeleteItem: handleDeleteItem,
      onStartEdit: startEdit,
      onEditFocused
    };
    $[25] = currentItemId;
    $[26] = editPrompt;
    $[27] = editingId;
    $[28] = handleDeleteItem;
    $[29] = historyRows;
    $[30] = onItemClick;
    $[31] = pendingFocusId;
    $[32] = saveEdit;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  const rowProps = t11;
  const isAuthenticated = !!user;
  const isHistoryHidden = sidebarState === "collapsed" && !isMobile;
  if (isUserLoading) {
    let t12;
    if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <SidebarHeader><div className="flex flex-col items-center gap-4 mb-4"><Link href="/" className="flex self-start items-center gap-2 my-2" prefetch={true}><Wordmark /><Badge>Image</Badge></Link></div><StudioNav /></SidebarHeader>;
      $[34] = t12;
    } else {
      t12 = $[34];
    }
    let t13;
    if ($[35] !== className) {
      t13 = <Sidebar className={className}>{t12}</Sidebar>;
      $[35] = className;
      $[36] = t13;
    } else {
      t13 = $[36];
    }
    return t13;
  }
  if (!isAuthenticated) {
    let t12;
    if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <Link href="/" className="flex self-start items-center gap-2 my-2" prefetch={true}><Wordmark /><Badge>Image</Badge></Link>;
      $[37] = t12;
    } else {
      t12 = $[37];
    }
    let t13;
    let t14;
    if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = <div className="font-medium mb-2">Sign in required</div>;
      t14 = <p className="text-muted-foreground mb-3">Please sign in to generate images.</p>;
      $[38] = t13;
      $[39] = t14;
    } else {
      t13 = $[38];
      t14 = $[39];
    }
    let t15;
    if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = <Button size="sm" asChild={true}><Link href="/login">Sign in</Link></Button>;
      $[40] = t15;
    } else {
      t15 = $[40];
    }
    let t16;
    if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
      t16 = <SidebarHeader><div className="flex flex-col items-center gap-4 mb-4">{t12}<div className="w-full rounded-md border p-4 text-sm">{t13}{t14}<div className="flex items-center justify-end gap-2">{t15}<Button size="sm" variant="outline" asChild={true}><Link href="/signup">Create account</Link></Button></div></div></div><StudioNav /></SidebarHeader>;
      $[41] = t16;
    } else {
      t16 = $[41];
    }
    let t17;
    if ($[42] !== className) {
      t17 = <Sidebar className={className}>{t16}</Sidebar>;
      $[42] = className;
      $[43] = t17;
    } else {
      t17 = $[43];
    }
    return t17;
  }
  const T0 = Sidebar;
  const t12 = "icon";
  const t13 = (className ?? "") + " max-md:hidden";
  const T1 = SidebarHeader;
  const T2 = SidebarMenu;
  const T3 = SidebarMenuItem;
  const T4 = SidebarMenuButton;
  const t14 = "lg";
  const t15 = true;
  const t16 = "Lounge";
  const T5 = Link;
  const t17 = withOrg("/");
  let t18;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <Wordmark size="sm" iconBox={true} />;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] !== T5 || $[46] !== t17) {
    t19 = <T5 href={t17} prefetch={true}>{t18}</T5>;
    $[45] = T5;
    $[46] = t17;
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  let t20;
  if ($[48] !== T4 || $[49] !== t19) {
    t20 = <T4 size={t14} asChild={t15} tooltip={t16}>{t19}</T4>;
    $[48] = T4;
    $[49] = t19;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  let t21;
  if ($[51] !== T3 || $[52] !== t20) {
    t21 = <T3>{t20}</T3>;
    $[51] = T3;
    $[52] = t20;
    $[53] = t21;
  } else {
    t21 = $[53];
  }
  let t22;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <SidebarChatSearch disabled={true} />;
    $[54] = t22;
  } else {
    t22 = $[54];
  }
  let t23;
  if ($[55] !== onNewChat) {
    t23 = <SidebarNewAction label="New Generation" onAction={onNewChat} />;
    $[55] = onNewChat;
    $[56] = t23;
  } else {
    t23 = $[56];
  }
  let t24;
  if ($[57] !== T2 || $[58] !== t21 || $[59] !== t23) {
    t24 = <T2>{t21}{t22}{t23}</T2>;
    $[57] = T2;
    $[58] = t21;
    $[59] = t23;
    $[60] = t24;
  } else {
    t24 = $[60];
  }
  let t25;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <StudioNav />;
    $[61] = t25;
  } else {
    t25 = $[61];
  }
  let t26;
  if ($[62] !== T1 || $[63] !== t24) {
    t26 = <T1>{t24}{t25}</T1>;
    $[62] = T1;
    $[63] = t24;
    $[64] = t26;
  } else {
    t26 = $[64];
  }
  let t27;
  if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <div className="mx-2 mb-2 border-t border-sidebar-border" />;
    $[65] = t27;
  } else {
    t27 = $[65];
  }
  let t28;
  if ($[66] !== onSelectOrganization || $[67] !== switcherOrganizations || $[68] !== switcherSelectedOrganization) {
    t28 = switcherOrganizations.length > 0 ? <SidebarMenu className="px-2 pb-2 group-data-[collapsible=icon]:hidden"><SidebarMenuItem><OrganizationSwitcher organizations={switcherOrganizations} selectedOrganization={switcherSelectedOrganization} onSelectOrganization={onSelectOrganization} /></SidebarMenuItem></SidebarMenu> : null;
    $[66] = onSelectOrganization;
    $[67] = switcherOrganizations;
    $[68] = switcherSelectedOrganization;
    $[69] = t28;
  } else {
    t28 = $[69];
  }
  let t29;
  if ($[70] !== t28) {
    t29 = <div>{t27}{t28}</div>;
    $[70] = t28;
    $[71] = t29;
  } else {
    t29 = $[71];
  }
  let t30;
  if ($[72] !== galleryItems || $[73] !== historyRows || $[74] !== isHistoryLoading || $[75] !== rowProps) {
    t30 = isHistoryLoading && galleryItems.length === 0 ? <HistorySkeleton withThumbnail={true} /> : galleryItems.length === 0 ? <div className="flex flex-col items-center justify-center py-8 text-center"><ImageIcon className="h-12 w-12 text-muted-foreground/50 mb-4" /><p className="text-sm text-muted-foreground mb-2">No generation history</p><p className="text-xs text-muted-foreground">Generate an image to see it here</p></div> : <List className="min-h-0 w-full flex-1" style={{
      width: "100%"
    }} rowComponent={ImageHistoryRowComponent} rowCount={historyRows.length} rowHeight={getImageHistoryRowHeight} rowProps={rowProps} overscanCount={8} />;
    $[72] = galleryItems;
    $[73] = historyRows;
    $[74] = isHistoryLoading;
    $[75] = rowProps;
    $[76] = t30;
  } else {
    t30 = $[76];
  }
  let t31;
  if ($[77] !== isHistoryHidden || $[78] !== t30) {
    t31 = <div ref={listContainerRef} aria-hidden={isHistoryHidden} className="flex min-h-0 flex-1 flex-col transition-opacity duration-200 ease-linear group-data-[collapsible=icon]:pointer-events-none group-data-[collapsible=icon]:opacity-0">{t30}</div>;
    $[77] = isHistoryHidden;
    $[78] = t30;
    $[79] = t31;
  } else {
    t31 = $[79];
  }
  let t32;
  if ($[80] !== t29 || $[81] !== t31) {
    t32 = <SidebarContent className="overflow-hidden pb-2">{t29}{t31}</SidebarContent>;
    $[80] = t29;
    $[81] = t31;
    $[82] = t32;
  } else {
    t32 = $[82];
  }
  let t33;
  if ($[83] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <SidebarLoungePoints />;
    $[83] = t33;
  } else {
    t33 = $[83];
  }
  const t34 = switcherSelectedOrganization ?? organization;
  const t35 = !switcherSelectedOrganization;
  let t36;
  if ($[84] !== isOrgLoading || $[85] !== t34 || $[86] !== t35) {
    t36 = <div className="group-data-[collapsible=icon]:hidden"><CreditsDisplay organization={t34} isLoading={isOrgLoading} isChatPlanOrg={t35} /></div>;
    $[84] = isOrgLoading;
    $[85] = t34;
    $[86] = t35;
    $[87] = t36;
  } else {
    t36 = $[87];
  }
  const t37 = user?.name ?? "User";
  let t38;
  if ($[88] !== user?.name) {
    t38 = user?.name?.split(" ").map(_temp6).join("").toUpperCase().slice(0, 2) ?? "U";
    $[88] = user?.name;
    $[89] = t38;
  } else {
    t38 = $[89];
  }
  let t39;
  if ($[90] !== t38) {
    t39 = <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground"><span className="text-xs font-semibold">{t38}</span></div>;
    $[90] = t38;
    $[91] = t39;
  } else {
    t39 = $[91];
  }
  const t40 = user?.name;
  let t41;
  if ($[92] !== t40) {
    t41 = <span className="truncate font-semibold">{t40}</span>;
    $[92] = t40;
    $[93] = t41;
  } else {
    t41 = $[93];
  }
  const t42 = user?.email;
  let t43;
  if ($[94] !== t42) {
    t43 = <span className="truncate text-xs text-muted-foreground">{t42}</span>;
    $[94] = t42;
    $[95] = t43;
  } else {
    t43 = $[95];
  }
  let t44;
  if ($[96] !== t41 || $[97] !== t43) {
    t44 = <div className="grid flex-1 text-left text-sm leading-tight">{t41}{t43}</div>;
    $[96] = t41;
    $[97] = t43;
    $[98] = t44;
  } else {
    t44 = $[98];
  }
  let t45;
  if ($[99] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = <ChevronUp className="ml-auto size-4 group-data-[collapsible=icon]:hidden" />;
    $[99] = t45;
  } else {
    t45 = $[99];
  }
  let t46;
  if ($[100] !== t37 || $[101] !== t39 || $[102] !== t44) {
    t46 = <DropdownMenuTrigger asChild={true}><SidebarMenuButton size="lg" tooltip={t37} className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground">{t39}{t44}{t45}</SidebarMenuButton></DropdownMenuTrigger>;
    $[100] = t37;
    $[101] = t39;
    $[102] = t44;
    $[103] = t46;
  } else {
    t46 = $[103];
  }
  let t47;
  let t48;
  if ($[104] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = <DropdownMenuItem asChild={true}><Link href="/pricing" prefetch={true}><CreditCard className="mr-2 h-4 w-4" />{"Membership & Billing"}</Link></DropdownMenuItem>;
    t48 = <DropdownMenuSeparator />;
    $[104] = t47;
    $[105] = t48;
  } else {
    t47 = $[104];
    t48 = $[105];
  }
  let t49;
  let t50;
  if ($[106] === Symbol.for("react.memo_cache_sentinel")) {
    t49 = <DropdownMenuItem asChild={true}><a href={process.env.NODE_ENV === "development" ? "http://localhost:3002/dashboard" : "https://llmgateway.io/dashboard"} target="_blank" rel="noopener noreferrer"><ExternalLink className="mr-2 h-4 w-4" />Dashboard</a></DropdownMenuItem>;
    t50 = <DropdownMenuSeparator />;
    $[106] = t49;
    $[107] = t50;
  } else {
    t49 = $[106];
    t50 = $[107];
  }
  let t51;
  if ($[108] === Symbol.for("react.memo_cache_sentinel")) {
    t51 = <span>Theme</span>;
    $[108] = t51;
  } else {
    t51 = $[108];
  }
  let t52;
  let t53;
  if ($[109] === Symbol.for("react.memo_cache_sentinel")) {
    t52 = <DropdownMenuItem className="justify-between gap-3" onSelect={_temp7}>{t51}<div onClick={_temp8} onKeyDown={_temp9}><ThemeToggle className="shrink-0" size="compact" /></div></DropdownMenuItem>;
    t53 = <DropdownMenuSeparator />;
    $[109] = t52;
    $[110] = t53;
  } else {
    t52 = $[109];
    t53 = $[110];
  }
  let t54;
  if ($[111] === Symbol.for("react.memo_cache_sentinel")) {
    t54 = <LogOut className="mr-2 h-4 w-4" />;
    $[111] = t54;
  } else {
    t54 = $[111];
  }
  let t55;
  if ($[112] !== logout) {
    t55 = <DropdownMenuContent className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg" side="top" align="end" sideOffset={4}>{t47}{t48}{t49}{t50}{t52}{t53}<DropdownMenuItem onClick={logout}>{t54}Log out</DropdownMenuItem></DropdownMenuContent>;
    $[112] = logout;
    $[113] = t55;
  } else {
    t55 = $[113];
  }
  let t56;
  if ($[114] !== t46 || $[115] !== t55) {
    t56 = <SidebarMenu><SidebarMenuItem><DropdownMenu>{t46}{t55}</DropdownMenu></SidebarMenuItem></SidebarMenu>;
    $[114] = t46;
    $[115] = t55;
    $[116] = t56;
  } else {
    t56 = $[116];
  }
  let t57;
  if ($[117] !== t36 || $[118] !== t56) {
    t57 = <SidebarFooter>{t33}{t36}{t56}</SidebarFooter>;
    $[117] = t36;
    $[118] = t56;
    $[119] = t57;
  } else {
    t57 = $[119];
  }
  let t58;
  if ($[120] !== T0 || $[121] !== t13 || $[122] !== t26 || $[123] !== t32 || $[124] !== t57) {
    t58 = <T0 collapsible={t12} className={t13}>{t26}{t32}{t57}</T0>;
    $[120] = T0;
    $[121] = t13;
    $[122] = t26;
    $[123] = t32;
    $[124] = t57;
    $[125] = t58;
  } else {
    t58 = $[125];
  }
  return t58;
}
function _temp9(event_1) {
  return event_1.stopPropagation();
}
function _temp8(event_0) {
  return event_0.stopPropagation();
}
function _temp7(event) {
  return event.preventDefault();
}
function _temp6(n) {
  return n[0];
}
function _temp5(group) {
  return group.items.length > 0;
}
function _temp4(a, b) {
  return b.timestamp - a.timestamp;
}
function _temp3(org) {
  return org.kind === "default";
}